import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/AuthContext";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Factory, MapPin, Scales, FileText, Eye,
  CheckCircle, XCircle, Clock, PencilSimple, Trash, User as UserIcon, Building, ArrowsLeftRight
} from "@phosphor-icons/react";
import ReassignDialog from "@/components/ReassignDialog";
import ObligationDetail from "@/pages/ObligationDetail";
import DocumentDetail from "@/pages/DocumentDetail";

const TABS = [
  { k: "info", label: "Información general" },
  { k: "obligations", label: "Obligaciones" },
  { k: "documents", label: "Documentos" },
];

function statusMeta(d) {
  const today = new Date().toISOString().slice(0, 10);
  const exp = d.expiration_date;
  if (!exp) return { label: "Sin vencimiento", cls: "bg-neutral-100 text-neutral-700", icon: Clock };
  if (exp < today) return { label: "Vencido", cls: "bg-rose-50 text-rose-700", icon: XCircle };
  const d30 = new Date(); d30.setDate(d30.getDate() + 30);
  if (exp <= d30.toISOString().slice(0, 10)) return { label: "Próximo a vencer", cls: "bg-amber-50 text-amber-700", icon: Clock };
  return { label: "Vigente", cls: "bg-emerald-50 text-emerald-700", icon: CheckCircle };
}

export default function InstallationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can, user } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState("info");
  const [inst, setInst] = useState(null);
  const [obligations, setObligations] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [openObligationId, setOpenObligationId] = useState(null);
  const [openDocumentId, setOpenDocumentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    Promise.all([
      api.get(`/installations/${id}`),
      api.get(`/installations/${id}/obligations`),
      api.get(`/installations/${id}/documents`),
    ]).then(([a, b, c]) => {
      if (!alive) return;
      setInst(a.data); setObligations(b.data || []); setDocuments(c.data || []);
    }).catch(e => {
      if (!alive) return;
      setErr(e?.response?.data?.detail || "No se pudo cargar la instalación");
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id, reloadKey]);

  const onDelete = async () => {
    if (!window.confirm("¿Eliminar esta instalación?")) return;
    try { await api.delete(`/installations/${id}`); toast.success("Eliminada"); nav("/instalaciones"); }
    catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
      if (e?.response?.status === 409 && isAdmin) setReassignOpen(true);
    }
  };

  if (loading) return <div className="text-center py-16 text-neutral-400" data-testid="installation-detail-loading">Cargando...</div>;
  if (err) return <div className="text-center py-16 text-rose-500" data-testid="installation-detail-error">{err}</div>;
  if (!inst) return null;

  const canEdit = can("installations", "write");
  const canDelete = can("installations", "delete");

  return (
    <div className="space-y-6 max-w-5xl" data-testid="installation-detail-page">
      <Link to="/instalaciones" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900" data-testid="back-to-installations">
        <ArrowLeft size={16}/> Volver a instalaciones
      </Link>

      {/* Header card */}
      <div className="bg-white border border-neutral-200 rounded-md p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-600 to-blue-700 text-white rounded-md flex items-center justify-center">
              <Factory size={26} weight="duotone"/>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Operación</div>
              <h1 className="font-display text-3xl font-black tracking-tighter mt-1" data-testid="installation-name">{inst.name}</h1>
              <div className="flex flex-wrap gap-2 mt-2">
                {inst.type && <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-semibold">{inst.type}</span>}
                {inst.code && <span className="px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded text-xs">Permiso: {inst.code}</span>}
                {inst.company?.name && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs font-semibold inline-flex items-center gap-1"><Building size={12}/> {inst.company.name}</span>}
              </div>
            </div>
          </div>
          {(canEdit || canDelete || isAdmin) && (
            <div className="flex gap-2">
              {isAdmin && (
                <button onClick={() => setReassignOpen(true)} className="inline-flex items-center gap-1 px-3 py-2 border border-blue-200 text-blue-700 rounded-md text-sm hover:bg-blue-50" data-testid="reassign-installation-btn">
                  <ArrowsLeftRight size={14} weight="bold"/> Reasignar
                </button>
              )}
              {canEdit && (
                <Link to={`/instalaciones?edit=${inst.id}`} className="inline-flex items-center gap-1 px-3 py-2 border border-neutral-300 rounded-md text-sm hover:bg-neutral-50" data-testid="edit-installation-link">
                  <PencilSimple size={14} weight="bold"/> Editar
                </Link>
              )}
              {canDelete && (
                <button onClick={onDelete} className="inline-flex items-center gap-1 px-3 py-2 border border-rose-200 text-rose-600 rounded-md text-sm hover:bg-rose-50" data-testid="delete-installation-btn">
                  <Trash size={14}/> Eliminar
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-neutral-200" data-testid="installation-tabs">
        {TABS.map(t => {
          const count = t.k === "obligations" ? obligations.length : t.k === "documents" ? documents.length : null;
          return (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.k ? "border-blue-600 text-blue-700" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}
              data-testid={`tab-${t.k}`}>
              {t.label}{count != null && <span className="ml-1 text-xs text-neutral-400">({count})</span>}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === "info" && <InfoTab inst={inst}/>}
      {tab === "obligations" && (
        openObligationId
          ? <ObligationDetail embeddedId={openObligationId} onBack={() => setOpenObligationId(null)} />
          : <ObligationsTab items={obligations} onOpen={setOpenObligationId}/>
      )}
      {tab === "documents" && (
        openDocumentId
          ? <DocumentDetail embeddedId={openDocumentId} onBack={() => setOpenDocumentId(null)} />
          : <DocumentsTab items={documents} onOpen={setOpenDocumentId}/>
      )}
      {reassignOpen && (
        <ReassignDialog kind="installation" sourceId={id} onClose={() => setReassignOpen(false)}
          onDone={(d) => { setReassignOpen(false); if (d.deleted_source) nav("/instalaciones"); else setReloadKey(k => k + 1); }}/>
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-[160px_1fr] gap-4 py-2 border-b border-neutral-100 last:border-b-0">
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{label}</div>
      <div className={`text-sm text-neutral-800 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function InfoTab({ inst }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-md p-6" data-testid="info-tab">
      <Row label="Nombre" value={inst.name}/>
      <Row label="Empresa" value={inst.company?.name}/>
      {inst.company?.tax_id && <Row label="RFC Empresa" value={inst.company.tax_id} mono/>}
      <Row label="Tipo" value={inst.type}/>
      <Row label="Permiso" value={inst.code} mono/>
      <Row label="Responsable" value={inst.responsible}/>
      <Row label="Dirección" value={inst.address}/>
      <Row label="Descripción" value={inst.description}/>
    </div>
  );
}

function ObligationsTab({ items, onOpen }) {
  if (items.length === 0) {
    return (
      <div className="bg-white border border-dashed border-neutral-300 rounded-md py-16 text-center text-neutral-400" data-testid="obligations-tab-empty">
        <Scales size={42} className="mx-auto mb-3 text-neutral-300" weight="duotone"/>
        <p>Sin obligaciones relacionadas con esta instalación.</p>
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="obligations-tab">
      {items.map(o => (
        <button key={o.id} type="button" onClick={() => onOpen(o.id)} className="text-left bg-white border border-neutral-200 rounded-md p-5 hover:border-blue-400 hover:-translate-y-0.5 transition-all" data-testid={`obligation-item-${o.id}`}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-50 text-emerald-700 rounded-md flex items-center justify-center"><Scales size={18} weight="duotone"/></div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-base font-bold tracking-tight line-clamp-2">{o.name}</h3>
              <div className="flex flex-wrap gap-1 mt-2">
                {o.authority && <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">{o.authority}</span>}
                {o.frequency && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">{o.frequency}</span>}
              </div>
              <div className="flex flex-wrap gap-1 mt-2 text-xs">
                {o.linked_directly && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded border border-emerald-100">Vinculada directamente</span>}
                {o.linked_via_documents && !o.linked_directly && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-100">Vía documentos</span>}
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function DocumentsTab({ items, onOpen }) {
  if (items.length === 0) {
    return (
      <div className="bg-white border border-dashed border-neutral-300 rounded-md py-16 text-center text-neutral-400" data-testid="documents-tab-empty">
        <FileText size={42} className="mx-auto mb-3 text-neutral-300" weight="duotone"/>
        <p>Sin documentos vinculados a esta instalación.</p>
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="documents-tab">
      {items.map(d => {
        const s = statusMeta(d);
        const Icon = s.icon;
        return (
          <button key={d.id} type="button" onClick={() => onOpen(d.id)} className="text-left bg-white border border-neutral-200 rounded-md p-5 hover:border-blue-400 hover:-translate-y-0.5 transition-all" data-testid={`document-item-${d.id}`}>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 bg-sky-50 text-sky-700 rounded-md flex items-center justify-center"><FileText size={18} weight="duotone"/></div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-sm font-bold tracking-tight line-clamp-2">{d.title || d.name || "Sin título"}</h3>
                <div className="flex flex-wrap gap-1 mt-1">
                  {d.authority && <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">{d.authority}</span>}
                  {d.period && <span className="px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded text-xs">{d.period}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${s.cls}`}><Icon size={12} weight="bold"/>{s.label}</span>
              {d.obligation_name
                ? <span className="text-neutral-500 truncate ml-2" title={d.obligation_name}>Obl: {d.obligation_name}</span>
                : <span className="text-neutral-400 italic ml-2">Sin obligación</span>}
            </div>
            {d.expiration_date && <div className="text-xs text-neutral-500 mt-2">Vence: {d.expiration_date}</div>}
          </button>
        );
      })}
    </div>
  );
}
